/*
  Eva Radio keyboard: wires the 29 SVG key hotspots (+ TONE toggle,
  + rhythm presets) to eva-synth.js. Pure interaction layer -- all actual
  sound comes from window.EvaSynth (assets/eva-synth.js).

  Keys + tape both work at once (no separate "mode" gate, removed per
  Per). Pointer/touch on the drawn keys is the ONLY way to play --
  computer-keyboard note triggering (the old A-K row + O/P octave
  shortcuts) was removed entirely per Per, to kill the whole class of
  "typing on the page plays notes" bugs for good. On-image hotspots are
  only reachable while eva-radio.js's angle-changer is showing 'above' --
  enforced implicitly by that photo's hotspot SVG being display:none
  otherwise (a hidden element doesn't receive pointer events at all, no
  JS visibility check needed here). Still listens for 'eva:radio:angle'
  to release any held notes when leaving 'above' (a still-held pointer
  shouldn't keep sounding once its key is no longer visible/reachable).
*/
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  var VOICE_KEY = 'evaRadioSynthVoice';

  var dragHandle = null;
  var keyboardSvg = null;
  var toneHotspot = null;
  var rhythmHotspots = [];
  var oneKeyPlayHotspots = [];
  var keyElements = []; // [{el, note}]

  // ONE KEY PLAY: both tan buttons are the same control (alternating
  // fingers between them is the real unit's own speed technique) --
  // pressing either advances the SAME shared step through one stored
  // melody, wrapping after the last note. A separate idle timer (not
  // tied to general keyboard activity) resets the step back to the top
  // after a pause, so a visitor who wanders off and comes back later
  // hears the melody from the start rather than picking up mid-phrase.
  // eva-synth.js's note parser only understands sharps, not flats -- Bb4
  // written as its enharmonic equivalent, A#4.
  var ONE_KEY_MELODY = ['F4', 'A4', 'C5', 'A4', 'F4', 'G4', 'A4', 'F4', 'G4', 'A4', 'A#4', 'G4', 'A4', 'G4', 'F4', 'F4'];
  var ONE_KEY_IDLE_RESET_MS = 8000;
  var oneKeyStep = 0;
  var oneKeyIdleTimer = null;
  var activeOneKeyPointers = {}; // pointerId -> { el } -- separate from activePointers so its ids ('okp:'-prefixed) never collide with regular keys' ('ptr:'-prefixed) in noteOff lookups

  function scheduleOneKeyIdleReset() {
    if (oneKeyIdleTimer) { clearTimeout(oneKeyIdleTimer); }
    oneKeyIdleTimer = setTimeout(function () { oneKeyStep = 0; }, ONE_KEY_IDLE_RESET_MS);
  }

  // TONE toggle click: reuses ui-hover.mp3 -- the same file sound-engine.js
  // plays (louder) for the header menu's own click confirmation -- rather
  // than a new asset. A dedicated Audio instance (not sound-engine.js's
  // shared one) so a header click firing around the same moment can't cut
  // this one's tail short, matching the pattern sound-engine.js itself
  // already uses for its own hover vs. click instances. Respects the
  // global mute (EvaMute.applyToElement, see toneClickAudio's creation
  // below) -- radio mechanical sounds were exempted from evaSoundMuted
  // before 2026-08-02's global-mute unification, no longer are.
  var TONE_CLICK_VOLUME = 0.18; // matches sound-engine.js's own CLICK_VOLUME -- same file, same target loudness
  var toneClickAudio = null;

  var onAboveAngle = false; // tracks eva:radio:angle purely to know when to release held notes

  // pointerId -> { note, el }, supports multi-touch chords
  var activePointers = {};

  function noteFromDataAttr(attr) {
    return attr.replace('s', '#'); // "Gs3" -> "G#3" (matches how the liquid markup encodes the sharp)
  }

  function isMuted() {
    return window.EvaMute ? window.EvaMute.isMuted() : false;
  }

  function loadVoice() {
    try {
      var v = localStorage.getItem(VOICE_KEY);
      return v === 'violinFlute' ? 'violinFlute' : 'fantasyPiano';
    } catch (e) { return 'fantasyPiano'; }
  }

  function saveVoice(v) {
    try { localStorage.setItem(VOICE_KEY, v); } catch (e) {}
  }

  // --- Duck UI blips AND the tape (a tad, per Per) while keys/rhythm are
  // active, same choke-point pattern as eva-radio.js's own
  // duckUiSound()/restoreUiSound() -- one active/idle transition dispatch
  // rather than firing on every single keypress. ----------------------

  var anyActive = false;
  function noteCountActive() {
    return Object.keys(activePointers).length + Object.keys(activeOneKeyPointers).length;
  }
  function updateDucking() {
    var active = noteCountActive() > 0;
    if (active === anyActive) { return; }
    anyActive = active;
    if (window.EvaSoundEngine) {
      if (active) { window.EvaSoundEngine.duck(0.3); } else { window.EvaSoundEngine.restore(); }
    }
    document.dispatchEvent(new CustomEvent('eva:radio:keyboard:active', { detail: { active: active } }));
  }

  // --- Key press/release -------------------------------------------

  function pressKey(id, note, el) {
    if (window.EvaSynth) { window.EvaSynth.noteOn(id, note); }
    if (el) {
      el.classList.add('eva-radio-key--pressed');
      // Rising-note glyph, same system eva-radio-viz.js already uses for
      // bass-reactive notes while a tape plays -- rises from the actual
      // pressed key instead of a speaker.
      if (window.EvaRadioViz) { window.EvaRadioViz.spawnKeyNote(el); }
    }
  }

  function releaseKey(id, el) {
    if (window.EvaSynth) { window.EvaSynth.noteOff(id); }
    if (el) { el.classList.remove('eva-radio-key--pressed'); }
  }

  function allKeysOff() {
    Object.keys(activePointers).forEach(function (pid) {
      releaseKey('ptr:' + pid, activePointers[pid].el);
    });
    activePointers = {};
    Object.keys(activeOneKeyPointers).forEach(function (pid) {
      releaseKey('okp:' + pid, activeOneKeyPointers[pid].el);
    });
    activeOneKeyPointers = {};
    keyElements.forEach(function (k) { k.el.classList.remove('eva-radio-key--pressed'); });
    oneKeyPlayHotspots.forEach(function (el) { el.classList.remove('eva-radio-key--pressed'); });
    if (window.EvaSynth) { window.EvaSynth.allNotesOff(); }
    anyActive = false;
    if (window.EvaSoundEngine) { window.EvaSoundEngine.restore(); }
    document.dispatchEvent(new CustomEvent('eva:radio:keyboard:active', { detail: { active: false } }));
  }

  // --- Pointer glissando: hit-test whatever key element is under the
  // pointer on every move, not just at pointerdown -- sliding a finger/
  // mouse across the keys plays each one in turn. ------------------

  function keyElementAtPoint(x, y) {
    var el = document.elementFromPoint(x, y);
    if (!el) { return null; }
    var keyEl = el.closest && el.closest('.eva-radio-key');
    return keyEl || null;
  }

  function onKeyPointerDown(e) {
    var el = e.currentTarget;
    var note = noteFromDataAttr(el.getAttribute('data-key-note'));
    var id = 'ptr:' + e.pointerId;
    try { el.setPointerCapture(e.pointerId); } catch (err) {}
    activePointers[e.pointerId] = { note: note, el: el };
    pressKey(id, note, el);
    updateDucking();
  }

  function onKeyPointerMove(e) {
    var entry = activePointers[e.pointerId];
    if (!entry) { return; }
    var overEl = keyElementAtPoint(e.clientX, e.clientY);
    if (overEl && overEl !== entry.el) {
      // glissando: release the old note, press the new one, same pointer id
      releaseKey('ptr:' + e.pointerId, entry.el);
      var note = noteFromDataAttr(overEl.getAttribute('data-key-note'));
      activePointers[e.pointerId] = { note: note, el: overEl };
      pressKey('ptr:' + e.pointerId, note, overEl);
    }
  }

  function onKeyPointerUp(e) {
    var entry = activePointers[e.pointerId];
    if (!entry) { return; }
    releaseKey('ptr:' + e.pointerId, entry.el);
    delete activePointers[e.pointerId];
    updateDucking();
  }

  // --- ONE KEY PLAY ----------------------------------------------------
  // Reuses the exact same pressKey/releaseKey pair regular keys use, so
  // it automatically gets the same voice behavior for free: fantasyPiano
  // decays on its own regardless of when releaseKey lands, violinFlute
  // sustains until it does -- matching "fantasyPiano: natural decay;
  // violinFlute: sustains until pointerup" without any extra branching
  // here. Same reason mobile already gets a decay voice by default: a
  // fresh session with no saved preference (loadVoice()'s own fallback)
  // is fantasyPiano, and taps release almost immediately either way.

  function onOneKeyPointerDown(e) {
    var el = e.currentTarget;
    var note = ONE_KEY_MELODY[oneKeyStep];
    oneKeyStep = (oneKeyStep + 1) % ONE_KEY_MELODY.length;
    scheduleOneKeyIdleReset();
    try { el.setPointerCapture(e.pointerId); } catch (err) {}
    activeOneKeyPointers[e.pointerId] = { el: el };
    pressKey('okp:' + e.pointerId, note, el);
    updateDucking();
  }

  function onOneKeyPointerUp(e) {
    var entry = activeOneKeyPointers[e.pointerId];
    if (!entry) { return; }
    releaseKey('okp:' + e.pointerId, entry.el);
    delete activeOneKeyPointers[e.pointerId];
    updateDucking();
  }

  // --- TONE toggle -----------------------------------------------------

  function applyVoiceUI(voice) {
    if (toneHotspot) { toneHotspot.setAttribute('data-active-voice', voice); }
  }

  function onToneClick() {
    if (toneClickAudio) {
      try {
        toneClickAudio.pause();
        toneClickAudio.currentTime = 0;
        toneClickAudio.volume = TONE_CLICK_VOLUME;
        var p = toneClickAudio.play();
        if (p && typeof p.catch === 'function') { p.catch(function () {}); }
      } catch (e) {}
    }
    var next = (window.EvaSynth && window.EvaSynth.getVoice() === 'violinFlute') ? 'fantasyPiano' : 'violinFlute';
    if (window.EvaSynth) { window.EvaSynth.setVoice(next); }
    saveVoice(next);
    applyVoiceUI(next);
  }

  // --- Rhythm presets ----------------------------------------------
  // Stubbed inert until Per supplies the seaweed_pt10 loop pack --
  // window.EvaRadioRhythmAssets stays empty (see layout/theme.liquid)
  // until those files exist, so this never attempts a broken fetch.

  function initRhythm() {
    var assets = window.EvaRadioRhythmAssets || {};
    var available = Object.keys(assets).length > 0;
    rhythmHotspots.forEach(function (el) {
      var key = el.getAttribute('data-eva-rhythm');
      if (!available || !assets[key]) {
        el.classList.add('eva-radio-hotspot--rhythm--inert');
        return;
      }
      el.addEventListener('click', function () {
        var isOn = window.EvaSynth.isRhythmPlaying(key);
        // tap the active one = stop; tap a different one = switch (stopRhythm of the old
        // key happens implicitly inside startRhythm, but only one preset plays at a time
        // here so explicitly clearing the lit state on every other hotspot too)
        rhythmHotspots.forEach(function (other) { other.setAttribute('data-active', '0'); });
        if (isOn) {
          window.EvaSynth.stopRhythm(key);
        } else {
          window.EvaSynth.stopAllRhythm();
          window.EvaSynth.startRhythm(key, assets[key]);
          el.setAttribute('data-active', '1');
        }
      });
    });
    if (!available) {
      // eslint-disable-next-line no-console
      console.info('[eva-radio] Rhythm section stubbed inert -- _source/radio/seaweed_pt10/ loop pack not found. Drop the 4 trimmed WAV loops into assets/ and wire window.EvaRadioRhythmAssets in theme.liquid to enable.');
    }
  }

  // --- Init --------------------------------------------------------------

  ready(function () {
    // Keyboard mode is a separate, non-critical subsystem -- a failure
    // here must never take down the radio's own transport (eva-radio.js
    // inits independently), so the whole body runs inside one try/catch.
    try {
      dragHandle = document.getElementById('EvaRadioDragHandle');
      keyboardSvg = document.getElementById('EvaRadioKeyboardHotspots');
      if (!dragHandle || !keyboardSvg) { return; }

      toneHotspot = keyboardSvg.querySelector('[data-eva-tone-toggle]');
      var soundAssets = window.EvaSoundAssets || {};
      if (soundAssets.hover) {
        toneClickAudio = new Audio(soundAssets.hover);
        toneClickAudio.preload = 'auto';
        if (window.EvaMute) { window.EvaMute.applyToElement(toneClickAudio); }
      }
      rhythmHotspots = Array.prototype.slice.call(keyboardSvg.querySelectorAll('[data-eva-rhythm]'));
      oneKeyPlayHotspots = Array.prototype.slice.call(keyboardSvg.querySelectorAll('[data-eva-onekeyplay]'));
      keyElements = Array.prototype.slice.call(keyboardSvg.querySelectorAll('.eva-radio-key')).map(function (el) {
        return { el: el, note: noteFromDataAttr(el.getAttribute('data-key-note')) };
      });

      if (window.EvaSynth) {
        window.EvaSynth.setVoice(loadVoice());
        window.EvaSynth.setMuted(isMuted());
      }
      applyVoiceUI(loadVoice());
      initRhythm();

      if (toneHotspot) { toneHotspot.addEventListener('click', onToneClick); }

      keyElements.forEach(function (k) {
        k.el.addEventListener('pointerdown', onKeyPointerDown);
        k.el.addEventListener('pointermove', onKeyPointerMove);
        k.el.addEventListener('pointerup', onKeyPointerUp);
        k.el.addEventListener('pointercancel', onKeyPointerUp);
      });

      oneKeyPlayHotspots.forEach(function (el) {
        el.addEventListener('pointerdown', onOneKeyPointerDown);
        el.addEventListener('pointerup', onOneKeyPointerUp);
        el.addEventListener('pointercancel', onOneKeyPointerUp);
      });
      // glissando hit-testing also needs move events from OTHER key elements
      // (a captured pointer keeps delivering move events only to the element
      // that captured it, which is exactly what we want -- the listener
      // above already covers this per-key, no extra document-level listener
      // needed).

      document.addEventListener('eva:radio:angle', function (e) {
        var wasAbove = onAboveAngle;
        onAboveAngle = !!(e.detail && e.detail.angle === 'above');
        if (wasAbove && !onAboveAngle) { allKeysOff(); } // leaving the view that shows the keys releases anything still held
      });

      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { allKeysOff(); }
      });

      document.addEventListener('eva:sound:muted', function (e) {
        if (window.EvaSynth) { window.EvaSynth.setMuted(!!(e.detail && e.detail.muted)); }
      });

      document.addEventListener('eva:pjax:loaded', function () {
        // dragHandle/keyboardSvg are outside #MainContent (survive PJAX
        // untouched), but re-run in case a future refactor changes that --
        // cheap no-op today, safety net later.
        if (!document.getElementById('EvaRadioKeyboardHotspots')) { allKeysOff(); }
      });
    } catch (kbErr) {
      console.error('[eva-radio-keyboard] init failed', kbErr);
    }
  });
})();
